import type { RdScheduleBlock } from "../../../shared/rd-schedule.js";
import type {
  RdTimeBlockCreateBody,
  RdTimeBlockInvalidResponse,
  RdTimeBlockKindName,
} from "../../../shared/api-types.js";
import { clockLabel, weekdayShort } from "./rd-schedule-week.js";

// NO-877: the pure core of the Block time dialog on the Week view. The form's
// defaults, the checks that run before a save, the request body and the
// sentence shown for every refusal live here, so the dialog only lays them
// out. A leaf module with relative imports: `tsx --test` reads it.

export const BLOCK_TIME_API_PATH = "/api/rd-schedule/blocks";

const STEP_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;
const DESCRIPTION_MAX_LENGTH = 500;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export type BlockTimeRepeat = "none" | "weekly";

export interface BlockTimeForm {
  kind: RdTimeBlockKindName;
  /** `YYYY-MM-DD`, or empty when the RD cleared the field. */
  date: string;
  /** Minutes after midnight, on the quarter hour. */
  startMinute: number;
  /** Minutes after midnight; 1440 is the end of the day. */
  endMinute: number;
  repeat: BlockTimeRepeat;
  description: string;
}

export type BlockTimeField = "kind" | "description" | "date" | "end" | "start";

export interface BlockTimeFieldError {
  field: BlockTimeField;
  message: string;
}

export interface BlockKindOption {
  kind: RdTimeBlockKindName;
  name: string;
}

/** The three choices, in the canvas's order. */
export const BLOCK_KIND_OPTIONS: readonly BlockKindOption[] = [
  { kind: "break", name: "Lunch or break" },
  { kind: "project", name: "Project" },
  { kind: "personal", name: "Personal" },
];

export const PROJECT_DESCRIPTION_LABEL = "What are you working on?";
export const PROJECT_DESCRIPTION_HINT =
  "Required. Your supervisor sees this. Project hours count separately from client-facing hours.";
export const LUNCH_HINT =
  "Your usual lunch lives in Availability so it repeats. Use this for a one-off change, like a later lunch on Tuesday.";
export const BLOCK_TIME_DESCRIPTION =
  "Nobody can book you during blocked time, including self-booking and Always AI.";

/** Today's date and the next quarter hour in `timeZone`. */
function localNow(nowIso: string, timeZone: string): { date: string; minute: number } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).formatToParts(new Date(nowIso));
  const read = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "00";

  return {
    date: `${read("year")}-${read("month")}-${read("day")}`,
    minute: (Number(read("hour")) % 24) * 60 + Number(read("minute")),
  };
}

/**
 * A fresh form: Project (the canvas's first view), today, the next quarter
 * hour for one hour, not repeating. Late in the day the hour is cut at
 * midnight, and the last quarter hour starts a quarter hour block.
 */
export function defaultBlockTimeForm(nowIso: string, timeZone: string): BlockTimeForm {
  const { date, minute } = localNow(nowIso, timeZone);
  const next = Math.ceil((minute + 1) / STEP_MINUTES) * STEP_MINUTES;
  const startMinute = Math.min(next, MINUTES_PER_DAY - STEP_MINUTES);

  return {
    kind: "project",
    date,
    startMinute,
    endMinute: Math.min(startMinute + 60, MINUTES_PER_DAY),
    repeat: "none",
    description: "",
  };
}

/** Every quarter hour a block can start: 12:00 AM to 11:45 PM. */
export const START_MINUTE_CHOICES: readonly number[] = Array.from(
  { length: MINUTES_PER_DAY / STEP_MINUTES },
  (_, index) => index * STEP_MINUTES
);

/** Every end after `startMinute`, up to the end of the day. */
export function endMinuteChoices(startMinute: number): number[] {
  const choices: number[] = [];

  for (let at = startMinute + STEP_MINUTES; at <= MINUTES_PER_DAY; at += STEP_MINUTES) {
    choices.push(at);
  }

  return choices;
}

/** "Eastern Time": the zone the form's times are in, named for a person. */
export function blockTimeZoneLabel(timeZone: string, nowIso: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "longGeneric",
  }).formatToParts(new Date(nowIso));

  return parts.find((part) => part.type === "timeZoneName")?.value ?? timeZone;
}

/** "3:45 PM"; the end of the day reads "12:00 AM" as the canvas has no 24th hour. */
export function blockTimeLabel(minute: number): string {
  return clockLabel(minute, true);
}

/** The value a time picker carries and the API takes: `HH:MM`, with `24:00` for the day's end. */
export function blockTimeValue(minute: number): string {
  const hours = String(Math.floor(minute / 60)).padStart(2, "0");
  const minutes = String(minute % 60).padStart(2, "0");

  return `${hours}:${minutes}`;
}

export function minuteFromBlockTimeValue(value: string): number {
  const [hours, minutes] = value.split(":").map(Number);

  return (hours ?? 0) * 60 + (minutes ?? 0);
}

/** Moving the start keeps the end; an end the start passes moves to keep the length. */
export function withStartMinute(
  form: BlockTimeForm,
  startMinute: number
): BlockTimeForm {
  const length = form.endMinute - form.startMinute;
  const endMinute =
    form.endMinute > startMinute
      ? form.endMinute
      : Math.min(startMinute + Math.max(length, STEP_MINUTES), MINUTES_PER_DAY);

  return { ...form, startMinute, endMinute };
}

/** The label for the repeat choice that names the weekday: "Every week on Tuesday". */
export function repeatLabel(repeat: BlockTimeRepeat, date: string): string {
  if (repeat === "none") return "Does not repeat";

  return ISO_DATE.test(date) ? `Every week on ${weekdayName(date)}` : "Every week";
}

const WEEKDAY_NAME: Record<string, string> = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

function weekdayName(date: string): string {
  return WEEKDAY_NAME[weekdayShort(date)] ?? "this day";
}

/** "1 hour", "30 minutes", "1 hour 15 minutes". */
export function durationText(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const hoursText = hours === 0 ? "" : `${hours} ${hours === 1 ? "hour" : "hours"}`;
  const restText = rest === 0 ? "" : `${rest} minutes`;

  return [hoursText, restText].filter(Boolean).join(" ");
}

/** The save button's tail: "Block 1 hour of project time", "Block the break". */
export function blockButtonLabel(form: BlockTimeForm): string {
  if (form.kind === "break") return "Block the break";

  if (form.kind === "personal") return "Block personal time";

  return `Block ${durationText(form.endMinute - form.startMinute)} of project time`;
}

/**
 * The checks that run before a save, in the order the fields appear. They
 * mirror the server's rules so most mistakes never leave the dialog; the
 * server stays the judge and its refusal is shown the same way.
 */
export function blockTimeErrors(form: BlockTimeForm): BlockTimeFieldError[] {
  const errors: BlockTimeFieldError[] = [];
  const description = form.description.trim();

  if (form.kind === "project" && description === "") {
    errors.push({
      field: "description",
      message: "Add what you're working on to save project time.",
    });
  }

  if (form.kind === "project" && description.length > DESCRIPTION_MAX_LENGTH) {
    errors.push({
      field: "description",
      message: `Shorten what you're working on to ${DESCRIPTION_MAX_LENGTH} characters or fewer.`,
    });
  }

  if (!ISO_DATE.test(form.date)) {
    errors.push({ field: "date", message: "Pick a date for this time." });
  }

  if (form.endMinute <= form.startMinute) {
    errors.push({ field: "end", message: "Pick an end time after the start time." });
  }

  return errors;
}

/** The POST body. Only project time carries a description; a weekly block repeats with no end date. */
export function blockTimeRequestBody(form: BlockTimeForm): RdTimeBlockCreateBody {
  return {
    kind: form.kind,
    date: form.date,
    start: blockTimeValue(form.startMinute),
    end: blockTimeValue(form.endMinute),
    repeat: form.repeat,
    ...(form.kind === "project" ? { description: form.description.trim() } : {}),
  };
}

export function blockTimeApiPath(rdId: string | null): string {
  return rdId
    ? `${BLOCK_TIME_API_PATH}?rdId=${encodeURIComponent(rdId)}`
    : BLOCK_TIME_API_PATH;
}

const REFUSAL_FIELD: Record<RdTimeBlockInvalidResponse["reason"], BlockTimeField> = {
  not_an_object: "kind",
  invalid_kind: "kind",
  invalid_date: "date",
  invalid_time: "start",
  end_not_after_start: "end",
  invalid_repeat: "kind",
  invalid_repeat_until: "date",
  description_required: "description",
  description_not_allowed: "description",
  description_too_long: "description",
};

export interface BlockTimeSaveFailure {
  /** Shown above the buttons. */
  message: string;
  /** The field the refusal is about, when the server named one. */
  field: BlockTimeField | null;
}

const SAVE_FAILED =
  "We couldn't save this time, so nothing was blocked. Try again in a moment.";

interface FailureShape {
  status: number | null;
  body: unknown;
}

function isInvalidBody(body: unknown): body is RdTimeBlockInvalidResponse {
  const candidate = body as Partial<RdTimeBlockInvalidResponse> | null | undefined;

  if (typeof candidate !== "object" || candidate === null) return false;

  const { error, message, reason } = candidate;
  const isNamed = error === "invalid_block" && typeof message === "string";

  return isNamed && typeof reason === "string" && reason in REFUSAL_FIELD;
}

/**
 * What the dialog says when a save fails: the server's own sentence for a
 * refused block, and a plain next step for everything else. `status` is null
 * when the request never reached the server.
 */
export function blockTimeSaveFailure(failure: FailureShape): BlockTimeSaveFailure {
  const { status, body } = failure;

  if (status === 422 && isInvalidBody(body)) {
    return { message: body.message, field: REFUSAL_FIELD[body.reason] };
  }

  if (status === null) {
    return {
      message:
        "We couldn't reach AlwaysAI, so nothing was blocked. Check your connection and try again.",
      field: null,
    };
  }

  return { message: statusMessage(status), field: null };
}

function statusMessage(status: number): string {
  if (status === 403) {
    return "You can't block time on this calendar. Ask a supervisor if you need it changed.";
  }

  if (status === 404) {
    return "Blocking time isn't available right now. Reload the page and try again.";
  }

  if (status === 409) {
    return "This RD is no longer active, so time can't be blocked on their calendar.";
  }

  if (status === 401) return "Your session ended. Sign in again, then block this time.";

  return SAVE_FAILED;
}

/** The DELETE path for one stored block; a repeating block goes in one call. */
export function blockRemoveApiPath(blockId: string, rdId: string | null): string {
  const base = `${BLOCK_TIME_API_PATH}/${encodeURIComponent(blockId)}`;

  return rdId ? `${base}?rdId=${encodeURIComponent(rdId)}` : base;
}

const KIND_NAME: Record<RdScheduleBlock["kind"], string> = {
  break: "Lunch or break",
  project: "Project time",
  personal: "Personal time",
};

/** "Project time, Thu, Oct 29, 4:00 PM to 5:00 PM", in the Week's zone. */
export function blockOccurrenceSummary(
  block: RdScheduleBlock,
  timeZone: string
): string {
  const day = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(new Date(block.startsAt));
  const clock = (instant: string): string =>
    new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(instant));

  return `${KIND_NAME[block.kind]}, ${day}, ${clock(block.startsAt)} to ${clock(block.endsAt)}`;
}

export const REMOVE_REPEAT_NOTE =
  "This time repeats every week. Removing it frees every week, past and future.";
export const REMOVE_ONCE_NOTE =
  "This frees the time so clients and Always AI can book it again.";

const REMOVE_FAILED =
  "We couldn't remove this time, so it is still blocked. Try again in a moment.";

/** The 404 answer that means this block is gone; any other 404 is the feature or the RD being unavailable. */
function isBlockNotFound(body: unknown): boolean {
  return (body as { error?: unknown } | null | undefined)?.error === "block_not_found";
}

/**
 * What the remove dialog says when the request fails; `status` is null when it
 * never reached the server. A 404 names a missing block only when the server
 * says so.
 */
export function blockRemoveFailure(status: number | null, body?: unknown): string {
  if (status === null) {
    return "We couldn't reach AlwaysAI, so this time is still blocked. Check your connection and try again.";
  }

  if (status === 404) {
    return isBlockNotFound(body)
      ? "This time was already removed. Close this to see your current week."
      : "Blocked time isn't available right now. Reload the page and try again.";
  }

  if (status === 403) {
    return "You can't change blocked time on this calendar. Ask a supervisor if it needs to go.";
  }

  if (status === 409) {
    return "This RD is no longer active, so their blocked time can't be changed.";
  }

  if (status === 401)
    return "Your session ended. Sign in again, then remove this time.";

  return REMOVE_FAILED;
}
